import { HttpClient } from '@angular/common/http';
import { effect, inject, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { API_URL } from '@core/services/api.tokens';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { Tenant } from '@shared/models/tenant.interface';
import { filter, map } from 'rxjs';
import { TenantFilterStore } from './tenant-filter.store';
import { tenantIdFromUrl } from './tenant-url';

interface TenantContextState {
  tenantId: string | null;
  isEvaluated: boolean | null;
}

const initialState: TenantContextState = {
  tenantId: null,
  isEvaluated: null,
};

/** The tenant the app is currently scoped to — driven by `/tenant/:tenantId`. */
export const TenantContextStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store) => {
    const router = inject(Router);
    const filters = inject(TenantFilterStore);
    const http = inject(HttpClient);
    const apiUrl = inject(API_URL);

    const readTenantIdFromRouter = (): string | null => {
      const navigation = router.getCurrentNavigation();
      const targetUrl = navigation?.extractedUrl
        ? router.serializeUrl(navigation.extractedUrl)
        : null;
      return tenantIdFromUrl(targetUrl ?? router.url);
    };

    return {
      /** Sync read for interceptors during an in-flight navigation. */
      currentTenantId(): string | null {
        return readTenantIdFromRouter() ?? store.tenantId();
      },

      setTenantId(tenantId: string | null) {
        const previous = store.tenantId();
        if (previous && tenantId && previous !== tenantId) {
          this.resetTenantScopedState(tenantId);
        }
        patchState(store, { tenantId });
      },

      syncFromUrl(url: string) {
        patchState(store, { tenantId: tenantIdFromUrl(url) });
      },

      /** Router commands for a page of the current tenant, e.g. `commands('settings', 'users')`. */
      commands(...segments: string[]): string[] {
        const tenantId = this.currentTenantId();
        return tenantId ? ['/tenant', tenantId, ...segments] : ['/tenant', ...segments];
      },

      resetTenantScopedState(tenantId: string) {
        filters.reset();
        filters.loadOrganisations(tenantId).subscribe();
      },

      setIsEvaluated(isEvaluated: boolean | null) {
        patchState(store, { isEvaluated });
      },

      loadTenant(tenantId: string | null) {
        if (!tenantId) {
          patchState(store, { isEvaluated: null });
          return;
        }
        http.get<Tenant>(`${apiUrl}/tenants/${tenantId}`, { withCredentials: true }).subscribe({
          next: (tenant) => patchState(store, { isEvaluated: tenant.is_evaluated ?? true }),
          error: () => patchState(store, { isEvaluated: null }),
        });
      },
    };
  }),
  withHooks({
    onInit(store) {
      const router = inject(Router);
      store.syncFromUrl(router.url);
      router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          map((event) => event.urlAfterRedirects),
        )
        .subscribe((url) => store.syncFromUrl(url));

      effect(() => {
        const tenantId = store.tenantId();
        untracked(() => store.loadTenant(tenantId));
      });
    },
  }),
);
